import React, { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Tag, CircleCheck, AlertCircle, Loader2, Plus } from 'lucide-react';
import { cn } from 'lib/utils';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import Skeleton from 'components/ui/skeleton';
import { RedeemPromocode } from 'modules/coins/api';
import { formatCoinCount, formatSom, walletCoverage } from './billingHelpers';

interface CoinWalletCardProps {
  balance: number;
  balanceLoading?: boolean;
  planName: string;
  /** What the next renewal costs (so'm), or null when nothing renews. */
  renewalPrice: number | null;
  /** Open the Buy Coins modal. */
  onTopUp: () => void;
}

/** The coin balance, how much of the next payment it covers, the top-up action and promo-code redemption. */
export function CoinWalletCard({ balance, balanceLoading = false, planName, renewalPrice, onTopUp }: CoinWalletCardProps) {
  const { t } = useTranslation();
  const [promoOpen, setPromoOpen] = useState(false);
  const promoFormId = useId();
  const coverage = walletCoverage(balance, renewalPrice);

  let coverageText: string;
  if (coverage.kind === 'max') {
    coverageText = t('coins_cover_max', {
      defaultValue: 'Your coins cover the maximum {{pct}}% ({{amount}}) of your {{plan}} renewal.',
      pct: coverage.pct,
      amount: formatSom(coverage.amount),
      plan: planName,
    });
  } else if (coverage.kind === 'partial') {
    coverageText = t('coins_cover', {
      defaultValue: 'Coins cover up to {{amount}} of your next {{plan}} payment.',
      amount: formatSom(coverage.amount),
      plan: planName,
    });
  } else {
    coverageText = t('coins_apply_at_checkout', 'Coins apply as a discount at checkout.');
  }

  return (
    <section className="rounded-lg border border-border bg-card px-5 py-4">
      <div className="flex flex-col items-stretch gap-4 md:flex-row md:items-center">
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <span className="text-[12.5px] leading-4 text-muted-foreground">
            {t('coin_wallet', 'Coin wallet')} · {t('billing_coin_rate', "1 coin = 1 so'm")}
          </span>
          {balanceLoading ? (
            <Skeleton className="h-6 w-32" />
          ) : (
            <span className="text-xl font-bold leading-6 tabular-nums text-foreground">
              {formatCoinCount(balance)}{' '}
              <span className="text-[13px] font-medium text-muted-foreground">{t('coins_unit', 'coins')}</span>
            </span>
          )}
          <p className="text-[13px] leading-[18px] text-foreground/80">{coverageText}</p>
          <button
            type="button"
            onClick={() => setPromoOpen((open) => !open)}
            aria-expanded={promoOpen}
            aria-controls={promoFormId}
            className="mt-1 inline-flex items-center gap-1.5 self-start rounded-sm text-[13px] font-semibold leading-4 text-foreground underline decoration-foreground/40 underline-offset-4 transition-colors hover:decoration-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <Tag className="h-3.5 w-3.5 shrink-0" aria-hidden />
            {t('redeem_a_code', 'Redeem a code')}
          </button>
        </div>
        <Button className="w-full shrink-0 gap-1.5 md:w-auto" onClick={onTopUp}>
          <Plus className="h-4 w-4" aria-hidden />
          {t('top_up_coins', 'Top up coins')}
        </Button>
      </div>
      {promoOpen && <PromoCodeForm id={promoFormId} />}
    </section>
  );
}

/** Redeem a promo code for coins; the wallet refetches on success. */
function PromoCodeForm({ id }: { id: string }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [code, setCode] = useState('');
  const [msg, setMsg] = useState<{ text: string; ok: boolean } | null>(null);

  const redeem = useMutation({
    mutationFn: async (value: string) => (await RedeemPromocode(value)).data,
    onSuccess: (data) => {
      setMsg({
        text: t('promo_redeemed_coins', {
          defaultValue: 'Code applied — {{coins}} coins added to your wallet.',
          coins: formatCoinCount(data.coinsGranted),
        }),
        ok: true,
      });
      setCode('');
      queryClient.invalidateQueries({ queryKey: ['coins'] });
    },
    onError: (err: unknown) => {
      const text =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        t('promo_redeem_failed', "We couldn't find that code. Check it and try again.");
      setMsg({ text, ok: false });
    },
  });

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (code.trim() && !redeem.isPending) redeem.mutate(code.trim());
  };

  return (
    <div className="mt-4 border-t border-border pt-4">
      <form id={id} onSubmit={submit} className="flex gap-2">
        <div className="relative min-w-0 flex-1">
          <Tag className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
          <Input
            value={code}
            onChange={(e) => {
              setCode(e.target.value.toUpperCase());
              setMsg(null);
            }}
            placeholder={t('promocode_placeholder', 'Enter promo code')}
            aria-label={t('promocode_placeholder', 'Enter promo code')}
            className="pl-9 font-mono uppercase"
          />
        </div>
        <Button type="submit" variant="outline" className="gap-1.5" disabled={!code.trim() || redeem.isPending}>
          {redeem.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />}
          {t('redeem', 'Redeem')}
        </Button>
      </form>
      {msg && (
        <p
          role="status"
          className={cn(
            'mt-2.5 flex items-center gap-1.5 text-[12.5px] font-medium',
            msg.ok ? 'text-success-700 dark:text-success-400' : 'text-error-700 dark:text-error-400',
          )}
        >
          {msg.ok ? <CircleCheck className="h-3.5 w-3.5 flex-none" aria-hidden /> : <AlertCircle className="h-3.5 w-3.5 flex-none" aria-hidden />}
          {msg.text}
        </p>
      )}
    </div>
  );
}

export default CoinWalletCard;
